// components/recipes/workflow/WorkflowSentenceView.tsx
//
// Pure, props-driven "workflow as a sentence" panel. Holds no business state —
// every option list and current value comes in via `model`, every change goes
// out via `handlers`. The store-backed container (WorkflowPanel) and the mock
// container (WorkflowSentence) both render this.
import React from 'react'
import classNames from 'classnames'
import { Search } from 'lucide-react'
import { ActionsList } from '../../../util/contstants'
import { Chip, ChipPopover, ChipOption } from './Chip'
import { DayOfWeekToggle, type Day } from './DayOfWeekToggle'
import {
    ScheduleEditor,
    type ScheduleEditorProps,
} from '../../../features/scheduled-segments/components/ScheduleEditor'
import {
    ACTION_CHIP_LABEL,
    CADENCES,
    NUMERIC_OPS,
    REPEAT_OPTIONS,
    RESTRICTIONS,
    STATE_OPTIONS,
    cadenceAllowsSchedule,
    executionModesFor,
    isNumericAction,
    isPercentOp,
    isStateAction,
    varLabel,
    valueChipText,
    type NumericOp,
    type ExecutionMode,
    type Repeat,
    type Restriction,
    type ValueSource,
} from './model'

export const PLACEMENTS = [
    { key: 'tos_placement', label: 'Top of search' },
    { key: 'ros_placement', label: 'Rest of search' },
    { key: 'pp_placement', label: 'Product pages' },
] as const

export type PlacementKey = (typeof PLACEMENTS)[number]['key']

export interface WorkflowViewModel {
    enabled: boolean
    // action
    actionOptions: { value: number; label: string }[]
    action: number
    // numeric value (bid / default bid / daily budget)
    op: NumericOp
    source: ValueSource
    rawValue: number // stored ×100
    variableName: string
    variableOptions: { name: string }[]
    variableAllowed: boolean // version === 2
    currency: string
    // state
    state: number
    strategy?: 'SALES_DOWN_ONLY' | 'SALES' | 'NONE'
    /** A missing key means "leave this placement unchanged"; 0 means "set it to 0%". */
    placements?: Partial<Record<PlacementKey, number>>
    // schedule (two axes: cadence + optional restriction)
    cadence: number
    restriction: Restriction
    days: Day[]
    date: Date
    repeat: Repeat
    /** Undefined keeps the legacy cadence editor available to old 2/4/6 rows. */
    executionMode?: ExecutionMode | null
    clockSchedulingAvailable: boolean
    scheduleEditor?: ScheduleEditorProps
    // precomputed summary (production wording)
    summary: string
}

export interface WorkflowViewHandlers {
    onToggleEnabled: () => void
    onExecutionMode?: (mode: ExecutionMode) => void
    onAction: (value: number) => void
    onOp: (op: NumericOp) => void
    onValue: (rawValue: number) => void // already ×100
    onSource: (source: ValueSource) => void
    onVariable: (name: string) => void
    onState: (state: number) => void
    onStrategy?: (strategy: 'SALES_DOWN_ONLY' | 'SALES' | 'NONE') => void
    /** `null` clears the placement, which leaves it unchanged when the recipe runs. */
    onPlacement?: (placement: PlacementKey, value: number | null) => void
    onCadence: (cadence: number) => void
    onRestriction: (restriction: Restriction) => void
    onDays: (days: Day[]) => void
    onDate: (date: Date) => void
    onRepeat: (repeat: Repeat) => void
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const toDateInputValue = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export const WorkflowSentenceView = ({
    model,
    handlers,
}: {
    model: WorkflowViewModel
    handlers: WorkflowViewHandlers
}) => {
    const m = model
    const h = handlers
    const showDays = m.restriction === 'days'
    const showDates = m.restriction === 'dates'
    const showSchedule = cadenceAllowsSchedule(m.cadence)
    const executionModes = executionModesFor({
        clockSchedulingAvailable: m.clockSchedulingAvailable,
    })
    const actionLabel = (v: number) =>
        ACTION_CHIP_LABEL[v] ?? m.actionOptions.find((a) => a.value === v)?.label ?? '—'

    return (
        <div className="flex h-full min-h-0 flex-col bg-white text-gray-800">
            {/* Header */}
            <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between border-b border-gray-200 bg-white px-3 py-3">
                <h3 className="m-0 flex items-center gap-2 text-[15px] font-semibold text-gray-900">
                    <LightningIcon className="h-4 w-4 text-blue-600" />
                    Workflow
                </h3>
                <button
                    type="button"
                    onClick={h.onToggleEnabled}
                    className={classNames(
                        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                        m.enabled
                            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
                    )}
                >
                    <span
                        className={classNames(
                            'h-1.5 w-1.5 rounded-full',
                            m.enabled ? 'bg-emerald-500' : 'bg-gray-400',
                        )}
                    />
                    {m.enabled ? 'Enabled' : 'Disabled'}
                </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="flex flex-col gap-3.5 px-3 py-3">
                    {/* ACTION */}
                    <SentenceRow keyword="Action">
                        <Chip
                            caret
                            content={
                                <ChipPopover>
                                    {m.actionOptions.map((a) => (
                                        <ChipOption
                                            key={a.value}
                                            label={actionLabel(a.value)}
                                            description={a.label}
                                            selected={m.action === a.value}
                                            onClick={() => h.onAction(a.value)}
                                        />
                                    ))}
                                </ChipPopover>
                            }
                        >
                            {actionLabel(m.action)}
                        </Chip>

                        {isNumericAction(m.action) && (
                            <>
                                <Connector />
                                <NumericValueChip model={m} handlers={h} />
                            </>
                        )}

                        {isStateAction(m.action) && (
                            <>
                                <Connector />
                                <Chip
                                    intent="value"
                                    caret
                                    dot={STATE_OPTIONS.find((s) => s.value === m.state)?.dot}
                                    content={
                                        <ChipPopover className="min-w-[180px]">
                                            {STATE_OPTIONS.map((s) => (
                                                <ChipOption
                                                    key={s.value}
                                                    label={
                                                        <span className="flex items-center gap-2">
                                                            <span
                                                                className={classNames(
                                                                    'h-1.5 w-1.5 rounded-full',
                                                                    s.dot,
                                                                )}
                                                            />
                                                            {s.label}
                                                        </span>
                                                    }
                                                    selected={m.state === s.value}
                                                    onClick={() => h.onState(s.value)}
                                                />
                                            ))}
                                        </ChipPopover>
                                    }
                                >
                                    {STATE_OPTIONS.find((s) => s.value === m.state)?.label ?? '—'}
                                </Chip>
                            </>
                        )}

                        {m.action === ActionsList.ChangeBiddingStrategy && (
                            <>
                                <Connector />
                                <Chip
                                    intent="value"
                                    caret
                                    content={
                                        <ChipPopover>
                                            {[
                                                ['SALES_DOWN_ONLY', 'Dynamic bids – down only'],
                                                ['SALES', 'Dynamic bids – up and down'],
                                                ['NONE', 'Fixed bids'],
                                            ].map(([value, label]) => (
                                                <ChipOption
                                                    key={value}
                                                    label={label}
                                                    selected={m.strategy === value}
                                                    onClick={() =>
                                                        h.onStrategy?.(
                                                            value as 'SALES_DOWN_ONLY' | 'SALES' | 'NONE',
                                                        )
                                                    }
                                                />
                                            ))}
                                        </ChipPopover>
                                    }
                                >
                                    {m.strategy === 'SALES'
                                        ? 'Dynamic bids – up and down'
                                        : m.strategy === 'NONE'
                                          ? 'Fixed bids'
                                          : 'Dynamic bids – down only'}
                                </Chip>
                            </>
                        )}

                        {m.action === ActionsList.ChangeBidAdjustment && (
                            <PlacementAdjustments model={m} handlers={h} />
                        )}
                    </SentenceRow>

                    {/* RUNS — cadence + a single rich schedule chip (like the value
                    chip): the chip summarises, its popover holds everything. */}
                    <SentenceRow keyword="Runs">
                        {m.executionMode !== undefined ? (
                            <Chip
                                caret
                                content={
                                    <ChipPopover className="min-w-[240px]">
                                        {executionModes.map((mode) => (
                                            <ChipOption
                                                key={mode.value}
                                                label={mode.label}
                                                description={mode.description}
                                                selected={m.executionMode === mode.value}
                                                onClick={() =>
                                                    m.executionMode !== mode.value &&
                                                    h.onExecutionMode?.(mode.value)
                                                }
                                            />
                                        ))}
                                    </ChipPopover>
                                }
                            >
                                {m.executionMode
                                    ? (executionModes.find((mode) => mode.value === m.executionMode)
                                          ?.label ?? '—')
                                    : 'Legacy schedule'}
                            </Chip>
                        ) : (
                            <>
                                <Chip
                                    caret
                                    content={
                                        <ChipPopover className="min-w-[240px]">
                                            {CADENCES.map((c) => (
                                                <ChipOption
                                                    key={c.value}
                                                    label={c.label}
                                                    description={c.description}
                                                    selected={m.cadence === c.value}
                                                    disabled={c.disabled}
                                                    onClick={() =>
                                                        !c.disabled && h.onCadence(c.value)
                                                    }
                                                />
                                            ))}
                                        </ChipPopover>
                                    }
                                >
                                    {CADENCES.find((c) => c.value === m.cadence)?.label ?? '—'}
                                </Chip>

                                {showSchedule && <ScheduleChip model={m} handlers={h} />}
                            </>
                        )}
                    </SentenceRow>

                    {m.executionMode === 'scheduled' && m.scheduleEditor && (
                        <ScheduleEditor {...m.scheduleEditor} />
                    )}
                </div>

                {/* Live summary — only for schedules complex enough to be worth
                    restating; simple frequencies are already obvious from the chips. */}
                {(showDays || showDates) && (
                    <div className="border-t border-gray-200 px-3 py-2.5">
                        <div className="flex items-start gap-2 rounded-md bg-gray-50 px-2.5 py-1.5">
                            <LightningIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <p className="m-0 text-[13px] leading-5 text-gray-600">
                                <span className="font-medium text-gray-800">Summary:</span>{' '}
                                {m.summary}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

// ── Numeric value chip (op + Value|Variable source + amount/variable) ────────
const NumericValueChip = ({
    model: m,
    handlers: h,
}: {
    model: WorkflowViewModel
    handlers: WorkflowViewHandlers
}) => {
    const [variableSearch, setVariableSearch] = React.useState('')
    const display = m.rawValue / 100
    const normalizedVariableSearch = variableSearch.trim().toLowerCase()
    const filteredVariableOptions = React.useMemo(() => {
        if (!normalizedVariableSearch) return m.variableOptions

        return m.variableOptions.filter((v) => {
            const rawName = v.name.toLowerCase()
            const displayName = varLabel(v.name).toLowerCase()
            return (
                rawName.includes(normalizedVariableSearch) ||
                displayName.includes(normalizedVariableSearch)
            )
        })
    }, [m.variableOptions, normalizedVariableSearch])

    return (
        <Chip
            intent="value"
            caret
            content={
                <ChipPopover className="min-w-[240px]">
                    <div className="px-1 pb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                        Change
                    </div>
                    {NUMERIC_OPS.map((o) => (
                        <ChipOption
                            key={o.value}
                            label={`${o.verb} (${o.unit})`}
                            selected={m.op === o.value}
                            onClick={() => h.onOp(o.value)}
                            dismiss={false}
                        />
                    ))}

                    {/* Value source segmented toggle */}
                    <div className="mt-2 border-t border-gray-100 px-1 pt-2">
                        <div className="pb-1 text-xs text-gray-500">Value from</div>
                        <div className="inline-flex rounded-md bg-gray-100 p-0.5">
                            {(['value', 'variable'] as ValueSource[]).map((s) => {
                                const disabled = s === 'variable' && !m.variableAllowed
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => h.onSource(s)}
                                        title={
                                            disabled ? 'Variables require a v2 segment' : undefined
                                        }
                                        className={classNames(
                                            'rounded px-2 py-0.5 text-xs font-medium capitalize transition-colors',
                                            m.source === s
                                                ? 'bg-white text-gray-900 shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700',
                                            disabled && 'cursor-not-allowed opacity-40',
                                        )}
                                    >
                                        {s}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    {/* Amount or variable picker */}
                    {m.source === 'value' ? (
                        <div className="mt-2 flex items-center gap-2 px-1">
                            <span className="text-xs text-gray-500">Amount</span>
                            <input
                                type="number"
                                value={display}
                                min={0}
                                step="0.01"
                                onChange={(e) =>
                                    h.onValue(Math.round(Number(e.target.value) * 100))
                                }
                                className="w-24 rounded-md border border-gray-200 px-2 py-1 text-[13px] focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                            />
                            <span className="text-xs text-gray-400">
                                {isPercentOp(m.op) ? '%' : m.currency}
                            </span>
                        </div>
                    ) : (
                        <div className="mt-2 px-1">
                            <div className="pb-1 text-xs text-gray-500">Variable</div>
                            {m.variableOptions.length === 0 ? (
                                <div className="rounded-md bg-gray-50 px-2 py-1.5 text-xs text-gray-400">
                                    No numeric variables defined
                                </div>
                            ) : (
                                <>
                                    <div className="relative mb-2">
                                        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="search"
                                            value={variableSearch}
                                            onChange={(e) => setVariableSearch(e.target.value)}
                                            onKeyDown={(e) => e.stopPropagation()}
                                            placeholder="Search variables"
                                            className="w-full rounded-md border border-gray-200 py-1.5 pl-7 pr-2 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                                        />
                                    </div>
                                    <div className="max-h-56 overflow-y-auto pr-1">
                                        {filteredVariableOptions.length === 0 ? (
                                            <div className="rounded-md bg-gray-50 px-2 py-1.5 text-xs text-gray-400">
                                                No variables match
                                            </div>
                                        ) : (
                                            filteredVariableOptions.map((v) => (
                                                <ChipOption
                                                    key={v.name}
                                                    label={varLabel(v.name)}
                                                    selected={m.variableName === v.name}
                                                    onClick={() => h.onVariable(v.name)}
                                                    dismiss={false}
                                                />
                                            ))
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </ChipPopover>
            }
        >
            {valueChipText(m.op, m.source, m.rawValue, m.variableName, m.currency)}
        </Chip>
    )
}

// ── Schedule chip (rich dropdown: restriction + days/dates in one popover) ──
const WEEK: Day[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const sameSet = (a: Day[], b: Day[]) => a.length === b.length && a.every((d) => b.includes(d))
const cap3 = (d: string) => d.charAt(0).toUpperCase() + d.slice(1, 3)

const daysSummary = (days: Day[]): string => {
    if (!days.length) return 'No days'
    if (days.length === 7) return 'Every day'
    if (sameSet(days, WEEK.slice(0, 5))) return 'Weekdays'
    if (sameSet(days, ['saturday', 'sunday'])) return 'Weekends'
    return [...days]
        .sort((a, b) => WEEK.indexOf(a) - WEEK.indexOf(b))
        .map(cap3)
        .join(', ')
}

const scheduleLabel = (m: WorkflowViewModel): string => {
    if (m.restriction === 'days') return daysSummary(m.days)
    if (m.restriction === 'dates') {
        const rep = REPEAT_OPTIONS.find((r) => r.value === m.repeat)?.label.toLowerCase() ?? ''
        return `${MONTHS[m.date.getMonth()]} ${m.date.getDate()} · ${rep}`
    }
    return 'Every day'
}

const ScheduleChip = ({
    model: m,
    handlers: h,
}: {
    model: WorkflowViewModel
    handlers: WorkflowViewHandlers
}) => (
    <Chip
        intent={m.restriction === 'none' ? 'default' : 'value'}
        caret
        content={
            <ChipPopover className="min-w-[248px]">
                <div className="px-1 pb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                    Run on
                </div>
                {RESTRICTIONS.map((r) => (
                    <ChipOption
                        key={r.value}
                        label={r.label}
                        description={r.description}
                        selected={m.restriction === r.value}
                        onClick={() => h.onRestriction(r.value)}
                        dismiss={false}
                    />
                ))}

                {m.restriction === 'days' && (
                    <div className="mt-2 border-t border-gray-100 px-1 pt-2">
                        <DayOfWeekToggle value={m.days} onChange={h.onDays} showError />
                    </div>
                )}

                {m.restriction === 'dates' && (
                    <div className="mt-2 flex flex-col gap-2 border-t border-gray-100 px-1 pt-2">
                        <div>
                            <div className="pb-1 text-xs text-gray-500">Starts on</div>
                            <input
                                type="date"
                                value={toDateInputValue(m.date)}
                                onChange={(e) => {
                                    // Native date inputs can be cleared to '' — ignore
                                    // that (and any non-parseable value) rather than
                                    // writing an Invalid Date to the recipe.
                                    const [y, mo, d] = e.target.value.split('-').map(Number)
                                    if (![y, mo, d].every(Number.isFinite)) return
                                    // Preserve time-of-day so the stored ISO timestamp only
                                    // shifts the calendar day (parity with the old DateInput).
                                    const next = new Date(m.date)
                                    next.setFullYear(y, mo - 1, d)
                                    h.onDate(next)
                                }}
                                className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-[13px] focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                            />
                        </div>
                        <div>
                            <div className="pb-1 text-xs text-gray-500">Repeats</div>
                            <select
                                value={m.repeat}
                                onChange={(e) => h.onRepeat(e.target.value as Repeat)}
                                className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[13px] focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                            >
                                {REPEAT_OPTIONS.map((r) => (
                                    <option key={r.value} value={r.value}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </ChipPopover>
        }
    >
        <CalendarIcon className="-ml-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
        {scheduleLabel(m)}
    </Chip>
)

/**
 * Placement bid adjustments. Each field is independent: a blank field is left
 * out of `extra`, and the recipe leaves that placement alone. A typed 0 is a
 * real value that resets the placement to 0%, so blank and 0 must never
 * collapse into each other here.
 */
const PlacementAdjustments = ({
    model: m,
    handlers: h,
}: {
    model: WorkflowViewModel
    handlers: WorkflowViewHandlers
}) => {
    const anySet = PLACEMENTS.some(({ key }) => typeof m.placements?.[key] === 'number')

    return (
        <div className="w-full rounded-lg border border-gray-200 bg-gray-50 p-2.5">
            <div className="flex flex-col gap-1.5">
                {PLACEMENTS.map(({ key, label }) => {
                    const value = m.placements?.[key]
                    const isSet = typeof value === 'number'

                    return (
                        <div key={key} className="flex items-center justify-between gap-2">
                            <span
                                className={classNames(
                                    'text-[13px]',
                                    isSet ? 'font-medium text-gray-800' : 'text-gray-500',
                                )}
                            >
                                {label}
                            </span>
                            <div className="relative shrink-0">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    aria-label={`${label} bid adjustment`}
                                    placeholder="Unchanged"
                                    value={isSet ? String(value) : ''}
                                    onChange={(event) => {
                                        // Digits only. An empty field clears the key,
                                        // which is how "unchanged" is stored.
                                        const digits = event.target.value.replace(/[^0-9]/g, '')
                                        if (digits === '') {
                                            h.onPlacement?.(key, null)
                                            return
                                        }
                                        h.onPlacement?.(
                                            key,
                                            Math.min(900, Number.parseInt(digits, 10)),
                                        )
                                    }}
                                    className={classNames(
                                        'w-[116px] rounded-md border py-1 text-right text-[13px] tabular-nums',
                                        'placeholder:text-[11px] placeholder:italic placeholder:text-gray-400',
                                        'focus:outline-none focus:ring-2 focus:ring-blue-400/40',
                                        isSet
                                            ? 'border-blue-200 bg-blue-50 pl-7 pr-5 font-medium text-blue-700 focus:border-blue-400'
                                            : 'border-gray-200 bg-white pl-2 pr-3 text-gray-700 focus:border-blue-400',
                                    )}
                                />
                                {isSet && (
                                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-[11px] text-blue-500">
                                        %
                                    </span>
                                )}
                                {isSet && (
                                    <button
                                        type="button"
                                        title={`Leave ${label.toLowerCase()} unchanged`}
                                        aria-label={`Leave ${label.toLowerCase()} unchanged`}
                                        onClick={() => h.onPlacement?.(key, null)}
                                        className="absolute left-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-blue-300 transition-colors hover:text-blue-600"
                                    >
                                        <svg
                                            viewBox="0 0 12 12"
                                            className="h-3 w-3"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        >
                                            <path d="M3 3l6 6M9 3l-6 6" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>

            <p
                className={classNames(
                    'm-0 mt-2 border-t pt-2 text-[11px] leading-4',
                    anySet ? 'border-gray-200 text-gray-500' : 'border-amber-200 text-amber-700',
                )}
            >
                {anySet
                    ? 'Leave blank for no change. Enter 0 to reset a placement to 0%.'
                    : 'Leave blank for no change. Enter a percentage for at least one placement.'}
            </p>
        </div>
    )
}

// ── Bits ────────────────────────────────────────────────────────────────────
const SentenceRow = ({ keyword, children }: { keyword: string; children: React.ReactNode }) => (
    <div className="flex flex-col gap-1">
        <span className="select-none text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {keyword}
        </span>
        <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
)

const Connector = () => <span className="select-none text-[13px] text-gray-300">→</span>

const CalendarIcon = ({ className = '' }: { className?: string }) => (
    <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <rect x="2.5" y="3" width="11" height="10.5" rx="1.5" />
        <path d="M2.5 6.5h11M5.5 1.5v3M10.5 1.5v3" />
    </svg>
)

const LightningIcon = ({ className = '' }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
)
